import { TableRowSideSheetForm } from "../../../../common/tables/view/TableRowSideSheetForm";
import React, {
    ChangeEvent,
    FC,
    PropsWithChildren,
    useMemo,
} from "react";
import { CostMetadataCreateInput } from "../../../../../../common/inputs/CostMetadataInput";
import { ShopifyProductVariant } from "../../../../../../common/models";
import { CostPayoutModifier } from "../../../../../../common/models";
import {
    Button,
    Checkbox,
    Heading,
    Label,
    Pane,
    PlusIcon,
    Portal,
    SelectField,
    SideSheet,
    Text,
    TextInputField,
} from "evergreen-ui";
import { DateTimeInputField } from "../../../../common/DateTimeInputField";

interface Props {
    isShown: boolean;
    costMetadata: Partial<CostMetadataCreateInput>;
    variants: ShopifyProductVariant[];
    costPayoutModifiers: CostPayoutModifier[];
    mutationLoading: boolean;
    mutationCalled: boolean;
    mutationError: boolean;
    onInputChanged: (costMetadata: Partial<CostMetadataCreateInput>) => void;
    onDismiss: () => void;
    onOpen: () => void;
    onSubmit: () => void;
}

export const CostMetadataTableRowSideSheetForm: FC<Props> = ({
    isShown,
    costMetadata,
    variants,
    costPayoutModifiers,
    mutationLoading,
    mutationCalled,
    mutationError,
    onInputChanged,
    onOpen,
    onDismiss,
    onSubmit,
}: PropsWithChildren<Props>) => {

    const onChange = ({ target: { value, name, type }}: ChangeEvent<HTMLInputElement>) => {
        onInputChanged({
            ...costMetadata,
            [name]: (type === "number") ? parseFloat(value) : value,
        });
    };

    const onSelectChange = ({ target: { value, name }}: ChangeEvent<HTMLSelectElement>) => {
        onInputChanged({
            ...costMetadata,
            [name]: value || undefined,
        });
    };

    const onVariantToggle = (variantId: string, checked: boolean) => {
        const current = costMetadata.variantIds ?? [];
        const updated = checked
            ? [...current, variantId]
            : current.filter(id => id !== variantId);
        onInputChanged({
            ...costMetadata,
            variantIds: updated,
        });
    };

    const isReadyToSubmit = useMemo((): boolean => {
        return !!(
            costMetadata.modifier &&
            costMetadata.shopifyAccountId &&
            costMetadata.startDate &&
            costMetadata.unitCost !== undefined &&
            costMetadata.juniperServiceFee !== undefined &&
            costMetadata.fulfillmentFee !== undefined &&
            costMetadata.processingFeePercent !== undefined &&
            costMetadata.shippingSubsidy !== undefined &&
            costMetadata.variantIds &&
            costMetadata.variantIds.length > 0
        );
    }, [costMetadata]);

    return (
        <>
            <Portal>
                <Button
                    minWidth={"160px"}
                    iconBefore={PlusIcon}
                    size={"large"}
                    appearance={"primary"}
                    position={"absolute"}
                    bottom={30}
                    right={30}
                    onClick={onOpen}
                    boxShadow={"0 4px 8px rgba(0, 0, 0, 0.2)"}
                >
                    Add Cost Metadata
                </Button>
            </Portal>
            <TableRowSideSheetForm
                isShown={isShown}
                headerTitle={"Add Cost Metadata"}
                mutationLoading={mutationLoading}
                mutationCalled={mutationCalled}
                mutationError={mutationError}
                canSubmit={isReadyToSubmit}
                onDismiss={onDismiss}
                onSubmit={onSubmit}
            >
                <TextInputField
                    name={"modifier"}
                    label={"Name"}
                    value={costMetadata.modifier ?? ""}
                    required={true}
                    maxWidth={"300px"}
                    isInvalid={!costMetadata.modifier}
                    onChange={onChange}
                />
                <TextInputField
                    name={"shopifyAccountId"}
                    label={"Shopify Account ID"}
                    value={costMetadata.shopifyAccountId ?? ""}
                    required={true}
                    maxWidth={"300px"}
                    isInvalid={!costMetadata.shopifyAccountId}
                    onChange={onChange}
                />
                <DateTimeInputField
                    label={"Start Date"}
                    value={costMetadata.startDate ?? null}
                    required={true}
                    maxWidth={"300px"}
                    isInvalid={!costMetadata.startDate}
                    onChange={(date) => {
                        onInputChanged({
                            ...costMetadata,
                            startDate: date?.toISOString() ?? undefined,
                        });
                    }}
                />
                <TextInputField
                    name={"unitCost"}
                    label={"Unit Cost"}
                    type={"number"}
                    value={costMetadata.unitCost}
                    required={true}
                    isInvalid={costMetadata.unitCost === undefined}
                    onChange={onChange}
                />
                <TextInputField
                    name={"juniperServiceFee"}
                    label={"Juniper Service Fee"}
                    type={"number"}
                    value={costMetadata.juniperServiceFee}
                    required={true}
                    isInvalid={costMetadata.juniperServiceFee === undefined}
                    onChange={onChange}
                />
                <TextInputField
                    name={"fulfillmentFee"}
                    label={"Fulfillment Fee"}
                    type={"number"}
                    value={costMetadata.fulfillmentFee}
                    required={true}
                    isInvalid={costMetadata.fulfillmentFee === undefined}
                    onChange={onChange}
                />
                <TextInputField
                    name={"processingFeePercent"}
                    label={"Processing Fee Percent"}
                    type={"number"}
                    value={costMetadata.processingFeePercent}
                    required={true}
                    isInvalid={costMetadata.processingFeePercent === undefined}
                    onChange={onChange}
                />
                <TextInputField
                    name={"shippingSubsidy"}
                    label={"Shipping Subsidy"}
                    type={"number"}
                    value={costMetadata.shippingSubsidy}
                    required={true}
                    isInvalid={costMetadata.shippingSubsidy === undefined}
                    onChange={onChange}
                />
                <SelectField
                    name={"costPayoutModifierId"}
                    label={"Cost Payout Modifier"}
                    value={costMetadata.costPayoutModifierId ?? ""}
                    onChange={onSelectChange}
                >
                    <option value={""}>None</option>
                    {costPayoutModifiers.map(cpm => (
                        <option key={cpm.id} value={cpm.id}>
                            {cpm.name}
                        </option>
                    ))}
                </SelectField>
                <Pane marginBottom={16}>
                    <Label display={"block"} marginBottom={4}>
                        Variants *
                    </Label>
                    {variants.length === 0 && (
                        <Pane paddingY={8} color={"muted"}>
                            No available variants
                        </Pane>
                    )}
                    {variants.map(variant => (
                        <Checkbox
                            key={variant.id}
                            label={
                                <Text size={300}>
                                    <strong>{variant.title || "Untitled variant"}</strong>
                                    {variant.sku && (
                                        <Text size={300} color={"muted"} marginLeft={4}>
                                            {variant.sku}
                                        </Text>
                                    )}
                                </Text>
                            }
                            checked={(costMetadata.variantIds ?? []).includes(String(variant.id))}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onVariantToggle(String(variant.id), e.target.checked)
                            }
                        />
                    ))}
                </Pane>
            </TableRowSideSheetForm>
        </>
    );
};

interface ViewSideSheetProps {
    isShown: boolean;
    title: string;
    onDismiss: () => void;
    children: React.ReactNode;
}

export const ViewSideSheet: FC<ViewSideSheetProps> = ({ isShown, title, onDismiss, children }) => {
    return (
        <SideSheet
            width={"360px"}
            isShown={isShown}
            onCloseComplete={onDismiss}
        >
            <Pane padding={"30px"}>
                <Heading marginBottom={"20px"}>{title}</Heading>
                {children}
            </Pane>
        </SideSheet>
    );
};
